import { use, useState } from 'react';

import { Grid } from '@mui/material';
import { Stack } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router';
import useSWR from 'swr';

import { ConfirmationDialog, FormDrawer, MapCard, SectionErrorBoundary } from '~components';
import VectorImage from '~components/media/vectorImage';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import { StatusVariant, UserRoleEnum } from '~enums';
import { CommandTypeEnum, isRelay1Action, isRelay2Action } from '~enums/commandTypeEnum';
import { Trigger, TriggersService, TriggerSourceType, TriggerTargetType } from '~features/triggers';
import useAuthorize from '~hooks/useAuthorize';
import { extendDevice } from '~interfaces/device';
import { getService } from '~services/serviceFactory';

import Dimens from '../../../themes/dimens';
import ControlBoxCard from '../components/cards/controlBoxCard';
import DeviceInformationCard from '../components/cards/deviceInformationCard';
import DeviceStateCard from '../components/cards/deviceStateCard';
import WallReaderCard from '../components/cards/wallReaderCard';
import DeviceActionsBar from '../components/deviceActionBar';
import LinkControlBoxForm, {
	LinkControlBoxFormValues,
} from '../components/forms/linkControlBoxForm';
import DeviceContext from '../providers/deviceContext';
import DevicesService from '../services/devicesService';

const deviceService = getService(DevicesService);
const triggersService = getService(TriggersService);

type UserAction = 'link' | 'viewDetails' | 'unlink';

const DeviceDetailsContainer = () => {
	const auth = useAuthorize();
	const deviceContext = use(DeviceContext);
	const deviceId = deviceContext.device?.id ?? '0';
	const organisationId = deviceContext.overwrittenClientId;
	const navigate = useNavigate();
	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);
	const isAuthorizedForRemoteActions = auth.authorize([
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.ClientAdmin,
		UserRoleEnum.Support,
		UserRoleEnum.Manufacturing,
	]);
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const [showDrawerLoader, setShowDrawerLoader] = useState(false);
	const [isUnlinking, setIsUnlinking] = useState(false);

	// Load device
	const {
		data: deviceData,
		error: _deviceError,
		isLoading: _deviceIsLoading,
	} = useSWR(deviceService.getDeviceByIdCacheKey(deviceId, organisationId), ([_, id, orgId]) =>
		id ? deviceService.getDeviceById(id, orgId ?? undefined) : null
	);
	const showConnectivityProfileSettingButton =
		extendDevice(deviceData)?.showConnectivityProfileSettingButton ?? false;

	const triggersCacheKey = [
		1,
		50,
		TriggerSourceType.DeviceTrigger,
		deviceId,
		organisationId ?? null,
	] as const;
	const {
		data: triggersData,
		isLoading: triggersLoading,
		mutate: triggersMutate,
	} = useSWR(triggersCacheKey, ([_pageNumber, _pageSize, triggerType, id, orgId]) => {
		return triggersService.getTriggers({
			pageNumber: 1,
			pageSize: 50,
			sourceType: triggerType,
			sourceId: id,
			deviceOrganisationId: orgId ?? undefined,
		});
	});

	// Load linked WallReader device
	const linkedWallReadersCacheKey = triggersService.getTriggersCacheKey(
		1,
		1,
		undefined,
		undefined,
		TriggerTargetType.DeviceTarget,
		deviceId,
		organisationId
	);
	const { data: linkedWallReaders } = useSWR(
		linkedWallReadersCacheKey,
		([_path, _pageNumber, _pageSize, _sourceType, _sourceId, _targetType, targetId, orgId]) => {
			if (targetId === undefined) return;
			return triggersService.getTriggers({
				pageNumber: 1,
				pageSize: 1,
				targetId: targetId,
				targetType: TriggerTargetType.DeviceTarget,
				deviceOrganisationId: orgId ?? undefined,
			});
		}
	);

	const linkedWallReaderId = linkedWallReaders?.results?.[0]?.deviceId;
	const { data: linkedWallReader } = useSWR(
		deviceService.getDeviceByIdCacheKey(linkedWallReaderId, organisationId),
		([_path, id, orgId]) => {
			if (id === undefined) return undefined;
			return deviceService.getDeviceById(id, orgId ?? undefined);
		}
	);

	const initialControlBoxId = triggersData?.results?.[0]?.target.id;
	const initialTrigger1 = triggersData?.results?.find((trigger) =>
		isRelay1Action(trigger.triggerDo)
	);
	const initialTrigger2 = triggersData?.results?.find((trigger) =>
		isRelay2Action(trigger.triggerDo)
	);
	const deviceDataExtended = extendDevice(deviceData);
	const showControlBoxCard = deviceDataExtended?.isWallReader ?? false;

	const handleLinkControlBox = async (values: LinkControlBoxFormValues) => {
		const isNewDevice = initialControlBoxId != values.controlBox.id;
		if (isNewDevice && initialControlBoxId) {
			await unlinkControlBox();
		}

		const promises = [];
		const action1Promise = getTriggerPromise(
			values.action1,
			initialTrigger1,
			isNewDevice,
			values.controlBox.id,
			'1'
		);
		const action2Promise = getTriggerPromise(
			values.action2,
			initialTrigger2,
			isNewDevice,
			values.controlBox.id,
			'2'
		);
		if (action1Promise) {
			promises.push(action1Promise);
		}
		if (action2Promise) {
			promises.push(action2Promise);
		}

		await Promise.all(promises).finally(() => {
			setShowDrawerLoader(false);
			clearUserAction();
			triggersMutate();
		});
	};

	// FIXME: Logic is hard to follow here 😅 I can do better.
	const getTriggerPromise = (
		newAction: CommandTypeEnum | undefined,
		initialTrigger: Trigger | undefined,
		isNewDevice: boolean,
		controlBoxId: string,
		triggerNumber: string
	): Promise<void> | undefined => {
		const service = triggersService;
		if (newAction != undefined) {
			let promise: Promise<void> | undefined;
			if (initialTrigger && !isNewDevice) {
				if (initialTrigger.triggerDo !== newAction) {
					promise = service.updateTrigger(
						initialTrigger.id,
						deviceId,
						controlBoxId,
						newAction,
						organisationId
					);
				}
			} else {
				promise = service.linkControlBox({
					sourceDeviceId: deviceId,
					targetDeviceId: controlBoxId,
					triggerDo: newAction,
					deviceOrganisationId: organisationId,
				});
			}

			if (promise) {
				return promise
					.then(() => {
						enqueueSnackbar(
							t('device.labels.relayLinkedSuccessfully', {
								ns: 'entities',
								relayNumber: triggerNumber,
							}),
							{ variant: 'success' }
						);
					})
					.catch((_error) => {
						enqueueSnackbar(
							t('device.labels.relayFailedToLink', { ns: 'entities', relayNumber: triggerNumber }),
							{ variant: 'error' }
						);
					});
			}
		} else {
			if (initialTrigger) {
				return triggersService
					.deleteTrigger(initialTrigger.id, organisationId)
					.then(() => {
						enqueueSnackbar(
							t('device.labels.relayUnlinkedSuccessfully', {
								ns: 'entities',
								relayNumber: triggerNumber,
							}),
							{ variant: 'success' }
						);
					})
					.catch((_error) => {
						enqueueSnackbar(
							t('device.labels.relayFailedToUnlink', {
								ns: 'entities',
								relayNumber: triggerNumber,
							}),
							{ variant: 'error' }
						);
					});
			}
		}

		return undefined;
	};

	const unlinkControlBox = async () => {
		setIsUnlinking(true);

		const promises: Promise<void>[] = [];
		let hadErrors = false;

		triggersData?.results.forEach((trigger: Trigger) => {
			promises.push(
				triggersService.deleteTrigger(trigger.id, organisationId).catch((_) => {
					enqueueSnackbar(t('triggerRemoveUnsuccessful', { ns: 'toast', triggerId: trigger.id }), {
						variant: 'error',
					});
					hadErrors = true;
				})
			);
		});

		await Promise.all(promises).finally(() => {
			if (!hadErrors) {
				enqueueSnackbar(t('deviceUnlinkSuccessful', { ns: 'toast' }), {
					variant: 'success',
				});
			}
			setIsUnlinking(false);
			clearUserAction();
			triggersMutate();
		});
	};

	const clearUserAction = () => {
		setUserAction(null);
	};

	return (
		<>
			<DeviceActionsBar
				deviceId={deviceId}
				organisationId={organisationId}
				showUnassignButton={
					isSuperAdmin &&
					organisationId != undefined &&
					organisationId != generalInformation.manufacturingOrganisationId
				}
				showAssignButton={
					isSuperAdmin && organisationId == generalInformation.manufacturingOrganisationId
				}
				showEditButton={isSuperAdmin}
				showChangeResourceGroupButton={
					isSuperAdmin && organisationId != generalInformation.manufacturingOrganisationId
				}
				showDeleteButton={isSuperAdmin}
				showRemoteActionsButton={isAuthorizedForRemoteActions}
				showConnectivityProfileSettingButton={showConnectivityProfileSettingButton}
			/>

			<Grid
				container
				columnSpacing={Dimens.spacing3xl}
				rowSpacing={Dimens.spacingXl}
				direction={'row'}
				sx={{ alignItems: 'stretch' }}
			>
				{showControlBoxCard && (
					<Grid size={12}>
						<ControlBoxCard
							isLoading={triggersLoading}
							deviceId={triggersData?.results[0]?.target.id}
							organisationId={organisationId}
							onLink={() => {
								setUserAction('link');
							}}
							onUnlink={() => {
								setUserAction('unlink');
							}}
							onViewDetails={() => {
								setUserAction('viewDetails');
							}}
						/>
					</Grid>
				)}
				{linkedWallReader && (
					<Grid size={12}>
						<WallReaderCard
							device={linkedWallReader}
							onViewDevice={() => {
								navigate({
									pathname: `/${pagePathSegments.Devices}/${linkedWallReader.id}`,
									search: createSearchParams({
										[queryParameters.OverwrittenClientId]: organisationId ?? '',
									}).toString(),
								});
							}}
						/>
					</Grid>
				)}
				<Grid size={{ xs: 12, md: 8 }}>
					<Stack spacing={Dimens.spacing3xl}>
						<DeviceStateCard deviceId={deviceId} organisationId={organisationId} />
						<DeviceInformationCard deviceId={deviceId} organisationId={organisationId} />
					</Stack>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<Stack spacing={Dimens.spacing3xl}>
						<SectionErrorBoundary>
							<MapCard
								title={t('lastKnownLocation', { ns: 'system' })}
								geoCoordinate={deviceData?.state?.location}
							/>
						</SectionErrorBoundary>
					</Stack>
				</Grid>
			</Grid>
			<FormDrawer
				anchor='right'
				loading={showDrawerLoader}
				open={['link', 'viewDetails'].includes(userAction ?? '')}
				onClose={clearUserAction}
			>
				<LinkControlBoxForm
					organisationId={organisationId}
					initialControlBoxId={triggersData?.results[0]?.target.id}
					initialAction1={initialTrigger1?.triggerDo}
					initialAction2={initialTrigger2?.triggerDo}
					onSubmit={(values: LinkControlBoxFormValues) => {
						handleLinkControlBox(values);
					}}
					onCancel={clearUserAction}
				/>
			</FormDrawer>
			<ConfirmationDialog
				open={userAction == 'unlink'}
				loading={isUnlinking}
				onClose={clearUserAction}
				icon={<VectorImage name='icon/unlink' />}
				title={t('device.labels.unlinkControlBoxTitle', { ns: 'entities' })}
				helperText={t('device.labels.unlinkControlBoxDescription', { ns: 'entities' })}
				confirmButtonText={t('unlink', { ns: 'actions' })}
				variant={StatusVariant.Destructive}
				onConfirm={unlinkControlBox}
			/>
		</>
	);
};

export default DeviceDetailsContainer;
